import { AppointmentFilter } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { GeneralFeedbackButton, StickyTopBar, useScrollToTop } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { theme } from '@frontend/theme';
import { Heading, Tabs } from '@frontend/design-system';
import { AppointmentAnalyticsBreadcrumb } from '../../atoms/appointment-analytics-breadcrumb';
import { APPOINTMENT_TABS, VIEW_KEYS } from '../../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import { AppointmentAnalyticsV2Filters } from '../appointment-analytics-v2-filters';
import { AppointmentAnalyticsV2TableFilters } from '../appointment-analytics-v2-table-filters';
import { CompareLocationsButton } from '../compare-locations-button';
import { PastAppointmentsTab } from '../past-appointments-tab';
import { UpcomingAppointmentsTab } from '../upcoming-appointments-tab';

export const AppointmentAnalyticsV2ViewContainer = () => {
  const { view, filtersInitialized, filterHintText, filters, activeTab, setActiveTab } =
    useAppointmentAnalyticsV2ShallowStore(
      'view',
      'activeTab',
      'setActiveTab',
      'filterHintText',
      'filters',
      'filtersInitialized'
    );

  const { t } = useTranslation('analytics');

  useScrollToTop([activeTab]);

  const { data: aggregatesData, isLoading } = AnalyticsGatewayV1.Queries.useGetAppointmentAggregatesQuery(
    {
      ...filters[activeTab],
      filter:
        activeTab === APPOINTMENT_TABS.UPCOMING
          ? AppointmentFilter.APPOINTMENT_FILTER_UPCOMING_APPOINTMENTS
          : AppointmentFilter.APPOINTMENT_FILTER_PAST_APPOINTMENTS,
    },
    {
      enabled: filtersInitialized && (filters[activeTab]?.locationId?.length ?? 0) > 0,
    }
  );

  return (
    <Page>
      <Page.Header>
        <AppointmentAnalyticsBreadcrumb />
        <Page.Header.Heading>
          <Page.Header.Title title={<Heading level={1}>{t('Appointment Analytics')}</Heading>} />
          <Page.Header.Subtitle
            subtitle={t('Showing results for {{filtersInfo}}.', {
              filtersInfo: filterHintText,
            })}
          />
          <Page.Header.Action>
            <div
              css={{
                display: 'flex',
                gap: theme.spacing(3),
                alignItems: 'center',
              }}
            >
              <GeneralFeedbackButton selectedLocationFromPageFilter={filters[activeTab]?.locationId} />
              <CompareLocationsButton />
            </div>
          </Page.Header.Action>
        </Page.Header.Heading>
      </Page.Header>
      <Page.Body>
        <Tabs controlledTab={activeTab} onChange={(id) => setActiveTab(id as APPOINTMENT_TABS)}>
          <StickyTopBar>
            <Tabs.Bar>
              <Tabs.Tab id={APPOINTMENT_TABS.UPCOMING}>{t('Upcoming Appointments')}</Tabs.Tab>
              <Tabs.Tab id={APPOINTMENT_TABS.PAST}>{t('Past Appointments')}</Tabs.Tab>
            </Tabs.Bar>
            {view === VIEW_KEYS.TABLE ? <AppointmentAnalyticsV2TableFilters /> : <AppointmentAnalyticsV2Filters />}
          </StickyTopBar>

          <Tabs.Panel controller={APPOINTMENT_TABS.UPCOMING}>
            <UpcomingAppointmentsTab
              aggregatesData={aggregatesData}
              aggregatesDataLoading={isLoading}
              containerStyles={viewRendererStyles}
            />
          </Tabs.Panel>

          <Tabs.Panel controller={APPOINTMENT_TABS.PAST}>
            <PastAppointmentsTab
              aggregatesData={aggregatesData}
              aggregatesDataLoading={isLoading}
              containerStyles={viewRendererStyles}
            />
          </Tabs.Panel>
        </Tabs>
      </Page.Body>
    </Page>
  );
};

const viewRendererStyles = {
  paddingTop: 'var(--fabric-space-gap-lg)',
};
